import { useState, useRef } from "react";
import AvatarEditor from "react-avatar-editor";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { useStyles } from "./styles";

interface Props {
  image: string;
  onSave: (blob: Blob) => void;
  onClose: () => void;
}

const CROSS_ORIGIN = "anonymous";

const ImageEditor = ({ image, onSave, onClose }: Props): JSX.Element => {
  const { t } = useI18n(["common", "errorPage"]);
  const theme = useTheme();
  const [zoomLevel, setZoomLevel] = useState<number>(1.0);
  const editorRef = useRef<AvatarEditor>(null);
  const { addErrorNotification } = useNotificationActions();
  const classes = useStyles();

  const onClickSave = () => {
    if (editorRef.current) {
      // If you want the image resized to the canvas size (also a HTMLCanvasElement)
      const canvasScaled = editorRef.current.getImageScaledToCanvas();

      if (!canvasScaled.toBlob) {
        addErrorNotification({
          message: t("errorPage:action.unsupported.browser"),
        });

        return;
      }
      canvasScaled.toBlob(
        (blob) => {
          if (blob) {
            onSave(blob);
          }
        },
        "image/png",
        1.0,
      );
    }
  };

  const sliderClasses = {
    track: {
      backgroundColor: theme.palette.primary.main,
      height: 10,
    },
    handle: {
      borderColor: theme.palette.primary.main,
      height: 20,
      width: 20,
      backgroundColor: "white",
    },
    rail: {
      borderColor: theme.palette.primary.main,
      height: 10,
    },
  };

  const onSliderChange = (level: number) => {
    setZoomLevel(level);
  };

  return (
    <div className={classes.container}>
      <div className={classes.avatarEditor}>
        <AvatarEditor
          className={classes.avatarCanvas}
          ref={editorRef}
          image={image}
          width={250}
          height={250}
          border={50}
          borderRadius={250}
          color={[255, 255, 255, 0.6]} // RGBA
          scale={zoomLevel}
          rotate={0}
          crossOrigin={CROSS_ORIGIN}
        />
        <Slider
          className={classes.avatarSlider}
          value={zoomLevel}
          onChange={onSliderChange}
          min={1.0}
          max={5.0}
          step={0.1}
          trackStyle={sliderClasses.track}
          handleStyle={sliderClasses.handle}
          railStyle={sliderClasses.rail}
        />
        <div className={classes.buttonsContainer}>
          <Button variant="secondary" size="lg" color="dark" onClick={onClose}>
            {t("button.cancel")}
          </Button>
          <Button
            autoFocus
            variant="primary"
            size="lg"
            color="blue"
            onClick={onClickSave}
            data-testid="save-button"
          >
            {t("button.save.photo")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
